// valid for authentication
import { SIGNUP_MESSAGES } from 'utils/constants/Messages'
import {
  ACCOUNT_URL,
  AUTH_SESSION_URL,
  BOOKED_APPOINTMENT,
  CLIENT_INFO_API,
  GRAPHQL_ENDPOINT,
  SHOPIFY_GRAPHQL_URL,
  SHOPIFY_STOREFRONT_ACCESS_TOKEN,
  SMS_SUBSCRIPTION_KLAVIYO_API,
  VERIFY_EMAIL_URL,
} from '../utils/constants/EndpointUrls'
import { AXIOS_METHODS } from '../utils/constants/Enums'
import {
  BOULEVARD_AUTH_API_TOKEN,
  CUSTOMER_SESSION_TOKEN,
} from '../utils/constants/Helpers'
import {
  cleanPhoneNumber,
  emailVerifier,
  getLocalstorageItem,
  tokenValidator,
} from '../utils/helper-functions'
import {
  ForgetPayload,
  ForgetPayloadResponse,
  KlaviyoSubscribePayload,
  LoginPayloadVariables,
  LoginSessionPayloadResponse,
  LoginSignupState,
  SignupPayload,
  SignUpPayloadResponse,
  SignUpVariable,
  UpdatePhoneNumberVariable,
  verifyEmailPayload,
  verifyEmailResponse,
} from '../utils/types/sessionTypes'
import {
  BookedAppointmentResponse,
  ClientInfoResponse,
  MyAppointmentsPayload,
  MyAppointmentsResponse,
  MyMembershipsPayload,
  MyMembershipsResponse,
} from '../utils/types/userTypes'
import { Axios } from './Axios'

/*
 * Query
 */

const myAppointmentsQuery = `query MyAppointments($query: QueryString){
  myAppointments(
    first: 2,
    query: $query
  ) {
    edges {
      node {
        id
        startAt
        endAt
        duration
        cancelled
        state
        clientId
        client {
          id
          name
        }
        location {
          id
          name
          businessName
        }
        notes
        appointmentServices {
          price
          startAt
          endAt
          startTimeOffset
          totalDuration
          duration
          staffId
          staff {
            id
            firstName
            lastName
            bio
          }
          serviceId
          service {
            name
            categoryId
            category {
              name
            }
          }
        }
      }
    }
  }
}`

const myMembershipsQuery = `query {
  myMemberships(first: 10) {
    edges {
      cursor
      node {
        termNumber
        startOn
        endOn
        clientId
        client {
          firstName
          lastName
        }
        status
        name
        vouchers {
          quantity
          service {
            id
            name
          }
        }
      }
    }
  }
}`

/*
 * Mutations
 */

const createUserMutation = `mutation customerCreate($input: CustomerCreateInput!) {
  customerCreate(input: $input) {
    customer {
      id
    }
    customerUserErrors {
      code
      field
      message
    }
  }
}`

const customerRecoverMutation = `mutation customerRecover($email: String!) {
  customerRecover(email: $email) {
    customerUserErrors {
      code
      field
      message
    }
  }
}`

/*
 * Api call
 */
export const emailVerification = async (emailId: string) => {
  if (!emailId) return
  const payload = {
    email: emailId,
  }
  const headers = {
    'Content-Type': 'application/json',
  }
  const isEmail = emailVerifier(emailId)
  if (isEmail) {
    const response = await Axios.setHeaders(headers).callApi<
      verifyEmailPayload,
      verifyEmailResponse
    >(AXIOS_METHODS.POST, VERIFY_EMAIL_URL, undefined, payload)
    if (response.data) {
      return response.data
    } else {
      throw new Error(response.message)
    }
  } else {
    throw new Error(SIGNUP_MESSAGES.INVALID_EMAIL)
  }
}

export const signUpCreateUserApi = async (variables: SignUpVariable) => {
  const headers = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_ACCESS_TOKEN,
  }
  const payload: SignupPayload = {
    query: createUserMutation,
    variables: { input: variables },
  }
  const response = await Axios.setHeaders(headers).callApi<
    SignupPayload,
    SignUpPayloadResponse
  >(AXIOS_METHODS.POST, SHOPIFY_GRAPHQL_URL, undefined, payload)
  if (response.data) {
    return response.data
  } else {
    throw new Error(response.message)
  }
}

export const updatePhoneNumber = async (
  authToken: string,
  variables: UpdatePhoneNumberVariable
) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getLocalstorageItem(CUSTOMER_SESSION_TOKEN)}`,
  }
  tokenValidator()
  const payload: UpdatePhoneNumberVariable = variables
  const response = await Axios.setHeaders(headers).callApi<
    UpdatePhoneNumberVariable,
    ClientInfoResponse
  >(AXIOS_METHODS.PUT, ACCOUNT_URL, undefined, payload)

  return response.data
}

export const getKlaviyoHeader: any = () => {
  const headerData = {
    accept: 'application/json',
    revision: process.env.REACT_APP_KLAVIYO_PUBLIC_REVISION,
    'content-type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }
  return headerData
}

export const sendSubscribeSMS = async (values: LoginSignupState) => {
  try {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'SMS Marketing Opt-In',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                first_name: values.firstName ?? 'NA',
                last_name: values.lastName ?? 'NA',
                phone_number: cleanPhoneNumber(values.mobileNumber!),
                subscriptions: {
                  sms: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: process.env.REACT_APP_KLAVIYO_RELATIONSHIP_ID!,
            },
          },
        },
      },
    }
    await Axios.setHeaders(getKlaviyoHeader()).callApi<
      KlaviyoSubscribePayload,
      string
    >(AXIOS_METHODS.POST, SMS_SUBSCRIPTION_KLAVIYO_API, undefined, payload)
  } catch (error) {
    console.error(error)
  }
}

export const getUserAuthSessionApi = async (
  authToken: string,
  variables: LoginPayloadVariables
) => {
  const payload: LoginPayloadVariables = variables
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Basic ${authToken}`,
  }
  const response = await Axios.setHeaders(headers).callApi<
    LoginPayloadVariables,
    LoginSessionPayloadResponse
  >(AXIOS_METHODS.POST, AUTH_SESSION_URL, undefined, payload)
  if (response) {
    return response
  } else {
    throw new Error(response)
  }
}

export const forgetPasswordApi = async (variables: verifyEmailPayload) => {
  const headers = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_ACCESS_TOKEN,
  }
  const payload: ForgetPayload = {
    query: customerRecoverMutation,
    variables,
  }
  const response = await Axios.setHeaders(headers).callApi<
    ForgetPayload,
    ForgetPayloadResponse
  >(AXIOS_METHODS.POST, SHOPIFY_GRAPHQL_URL, undefined, payload)
  if (response.data) {
    return response.data
  } else {
    throw new Error(response.message)
  }
}

export const getMyAppointments = async (query: string) => {
  const headers = {
    Authorization: `Basic ${getLocalstorageItem(BOULEVARD_AUTH_API_TOKEN)}`,
  }
  const payload: MyAppointmentsPayload = {
    query: myAppointmentsQuery,
    variables: {
      query,
    },
  }
  const response = await Axios.setHeaders(headers).callApi<
    MyAppointmentsPayload,
    MyAppointmentsResponse
  >(AXIOS_METHODS.POST, GRAPHQL_ENDPOINT, undefined, payload)
  if (response.data) {
    return response.data
  } else {
    throw new Error(response.message)
  }
}

export const getMyMemberships = async () => {
  const headers = {
    Authorization: `Basic ${getLocalstorageItem(BOULEVARD_AUTH_API_TOKEN)}`,
  }
  const payload = {
    query: myMembershipsQuery,
  }
  const response = await Axios.setHeaders(headers).callApi<
    MyMembershipsPayload,
    MyMembershipsResponse
  >(AXIOS_METHODS.POST, GRAPHQL_ENDPOINT, undefined, payload)
  if (response.data) {
    return response.data
  } else {
    throw new Error(response.message)
  }
}

export const getUserInfo = async () => {
  tokenValidator()
  const headers = {
    Authorization: `Bearer ${getLocalstorageItem(CUSTOMER_SESSION_TOKEN)}`,
  }
  const response = await Axios.setHeaders(headers).callApi<
    undefined,
    ClientInfoResponse
  >(AXIOS_METHODS.GET, CLIENT_INFO_API)
  if (response.data) {
    return response.data
  } else {
    throw new Error(response.message)
  }
}

export const getUserAppointments = async () => {
  tokenValidator()
  const headers = {
    Authorization: `Bearer ${getLocalstorageItem(CUSTOMER_SESSION_TOKEN)}`,
  }
  const response = await Axios.setHeaders(headers).callApi<
    undefined,
    BookedAppointmentResponse[]
  >(AXIOS_METHODS.GET, `${BOOKED_APPOINTMENT}?recent=true`)
  if (response.data) {
    return response.data
  } else {
    throw new Error(response.message)
  }
}
